import { Link } from "react-router-dom";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

const problemHooks = [
  "Students forget what they cannot visualize.",
  "Histology lectures are too passive.",
  "Microscopy access is limited, expensive, and inconsistent.",
  "Exam prep is disconnected from real tissue understanding.",
];

const solutionHooks = [
  {
    title: "Professor Control System",
    desc: "Build slide-based quizzes in minutes and monitor engagement from one dashboard.",
  },
  {
    title: "Virtual Lab Teaching",
    desc: "Replace microscope sessions with zoomable digital slides and standardize teaching.",
  },
  {
    title: "AI Teaching Support",
    desc: "Ask AI to explain tissues instantly and generate clearer teaching explanations.",
  },
  {
    title: "Presentation Builder",
    desc: "Turn slides and 3D models into ready-to-teach lessons without starting from scratch.",
  },
];

const featureHooks = [
  {
    title: "Create Exams from Real Slides",
    desc: "Turn histology slides into interactive quizzes instantly.",
  },
  {
    title: "Teach Like a Microscope in Every Student's Pocket",
    desc: "Deliver digital precision without physical lab bottlenecks.",
  },
  {
    title: "AI That Explains Every Tissue",
    desc: "Get instant clarification for difficult microanatomy in class.",
  },
  {
    title: "Build Presentations from Real Medical Data",
    desc: "No manual slide design needed for each lecture.",
  },
];

export default function LandingPage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 600px at 20% -10%, rgba(33,150,243,0.18), transparent), radial-gradient(1000px 500px at 90% -20%, rgba(156,39,176,0.18), transparent), #0b1220",
        color: "#fff",
        py: { xs: 5, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Stack spacing={2.2} sx={{ height: "100%", justifyContent: "center" }}>
              <Card
                sx={{
                  p: { xs: 2.2, md: 3 },
                  borderRadius: 3,
                  backgroundImage:
                    "linear-gradient(120deg, rgba(7,16,36,0.88), rgba(7,16,36,0.74)), url('/landing-doctor.png')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <Stack spacing={2}>
                  <Chip
                    label="Professor Dashboard"
                    sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff", width: "fit-content" }}
                  />
                  <Typography variant="h2" sx={{ fontWeight: 800, letterSpacing: -0.8, color: "#fff" }}>
                    Stop explaining histology.
                    <br />
                    Start letting students see it.
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#fff", fontWeight: 400 }}>
                    Build quizzes, presentations, and interactive learning directly from real
                    histology tissue - no microscope required.
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#fff" }}>
                    Microlab is a virtual histology teaching system that combines zoomable slides,
                    AI explanation, 3D anatomy, and exam-ready workflows in one platform.
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      component={Link}
                      to="/authentication/sign-in"
                      variant="contained"
                      size="large"
                      sx={{
                        px: 3.5,
                        py: 1.2,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#1e88e5",
                      }}
                    >
                      Login to Dashboard
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        <Card
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.16)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 2 }}>
            Why professors switch to Microlab
          </Typography>
          <Grid container spacing={1.2}>
            {problemHooks.map((item) => (
              <Grid key={item} item xs={12} md={6}>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  - {item}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Card>

        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          {solutionHooks.map((feature) => (
            <Grid key={feature.title} item xs={12} sm={6}>
              <Card
                sx={{
                  height: "100%",
                  p: 2.2,
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.86)" }}>
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 1.5 }}>
          Feature highlights
        </Typography>
        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          {featureHooks.map((feature) => (
            <Grid key={feature.title} item xs={12} sm={6}>
              <Card
                sx={{
                  height: "100%",
                  p: 2.2,
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.86)" }}>
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            bgcolor: "rgba(30,136,229,0.16)",
            border: "1px solid rgba(30,136,229,0.4)",
          }}
        >
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800, mb: 1 }}>
            Outcome for your course
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.92)", lineHeight: 1.9 }}>
            Faster lecture preparation - Higher student engagement - Better exam performance -
            Standardized histology teaching - Less dependency on physical lab resources.
          </Typography>
        </Card>
      </Container>
    </Box>
  );
}
